import * as v from 'valibot';
import {
  buildSortPlansPayloads,
  FIRST_LOAN_TIME_VALUES,
  isCurrentPlanRow,
  LAST_LOAN_TIME_VALUES,
  parseResultSections,
  type Sf0726FormModel,
  type Sf0726LoanInput,
  type Sf0726ResultSection,
} from 'tio-employee/utils/strategy-finder-0726';

export {
  loanInputsFromSummary,
  withTestLoanCards,
  answerProfilePrefill,
  wellnessNsldsPrefill,
  CURRENT_PLAN_OPTION_KEYS,
} from 'tio-employee/utils/strategy-finder-0726';
export type { Sf0726LoanInput, Sf0726NsldsPrefill } from 'tio-employee/utils/strategy-finder-0726';

export const REQUESTING_APP = 'PSLF-0726';

// The fields the user actually fills in — all hardcoded assumptions are excluded.
export type Fe0726FormModel = {
  agi: number; // cents
  numberOfAdults: number;
  numberOfChildren: number;
  firstLoanTime: string;
  lastLoanTime: string;
  madePayments: number; // PSLF qualifying payments made
};

export const fe0726FormSchema = v.object({
  agi: v.pipe(
    v.number('Income must be greater than $0'),
    v.minValue(1, 'Income must be greater than $0')
  ),
  numberOfAdults: v.pipe(
    v.number('Please select a response'),
    v.picklist([1, 2], 'Please select a response')
  ),
  numberOfChildren: v.pipe(v.number('Must be 0 or greater'), v.minValue(0, 'Must be 0 or greater')),
  firstLoanTime: v.pipe(
    v.string('Please select a response'),
    v.picklist([...FIRST_LOAN_TIME_VALUES], 'Please select a response')
  ),
  lastLoanTime: v.pipe(
    v.string('Please select a response'),
    v.picklist([...LAST_LOAN_TIME_VALUES], 'Please select a response')
  ),
  madePayments: v.pipe(v.number('Please enter a number'), v.minValue(0, 'Must be 0 or greater')),
});

// Builds the full SF-0726 payload, filling in all hardcoded assumptions so they
// are never surfaced to the user but still reach the backend correctly.
export function buildFe0726Payloads(model: Fe0726FormModel, loanInputs: Sf0726LoanInput[]) {
  const fullModel: Sf0726FormModel = {
    payoffGoal: 'slow',
    affordPayments: 'no',
    agi: model.agi,
    numberOfAdults: model.numberOfAdults,
    numberOfChildren: model.numberOfChildren,
    firstLoanTime: model.firstLoanTime,
    lastLoanTime: model.lastLoanTime,
    residenceState: 'CA',
    currentlyWorkingToward: 'yes',
    currentForgivenessProgram: 'PublicServiceLoanForgiveness',
    madePayments: model.madePayments,
    isGov: true,
    isTeacher: false,
    isOther: false,
    planInGov: 'for-10-years-more',
    planAsTeacher: '',
    employmentMonths: 0,
    tlfQualifies17500: false,
  };
  return buildSortPlansPayloads(fullModel, loanInputs);
}

// Filter parsed result sections down to the PSLF-eligible plan rows. Non-PSLF
// plans (Standard, Extended, Graduated, etc.) are excluded because this tool is
// specifically for users working toward PSLF. Rows where pslf_forgiveness === 0
// ("nothing left to forgive") are retained — pslf === true still holds for them.
//
// Two non-PSLF rows are always kept so the table never hides them:
//   - the best plan (the first row, which the engine pre-sorted to the top), and
//   - the borrower's current plan,
// because LoanPlansSection derives "best plan" from plans[0] and the current
// plan via isCurrentPlanRow, and the comparison table above always shows both.
export function pslfOnlySections(sections: Sf0726ResultSection[]): Sf0726ResultSection[] {
  return sections.map((section) => {
    const bestRow = section.plans[0];
    return {
      ...section,
      plans: section.plans.filter(
        (row) => row.pslf || row === bestRow || isCurrentPlanRow(row, section)
      ),
    };
  });
}

// Parse and filter results in one call, used by the results-table component.
export function parseAndFilterResultSections(
  calculationInput: Record<string, unknown> | undefined,
  calculationOutput: Record<string, unknown> | undefined
): Sf0726ResultSection[] {
  return pslfOnlySections(parseResultSections(calculationInput, calculationOutput));
}

// Only persists the fields the user actually entered. Hardcoded assumptions
// (payoffGoal, affordPayments, residenceState, employment, forgiveness program,
// etc.) are intentionally omitted so the user's real answers in other tools
// are not overwritten.
export function fe0726AnswerProfileUpdates(model: Fe0726FormModel) {
  return {
    householdIncome: model.agi,
    numberOfAdults: model.numberOfAdults,
    numberOfChildren: model.numberOfChildren,
    householdSize: model.numberOfAdults + model.numberOfChildren,
    federalFirstLoanTime: model.firstLoanTime,
    federalLastLoanTime: model.lastLoanTime,
    madePayments: model.madePayments,
  };
}
